import { Button, Stack, Typography } from '@mui/material';
import { Trans, useTranslation } from 'react-i18next';
import { enqueueSnackbar } from 'notistack';
import { useMutation } from '@tanstack/react-query';

import { useInvalidateExternalGeoEventQuery } from 'hooks/useExternalGeoEvents';
import { ExternalGeoEvent, RequestedAck } from 'shared/types';
import { useAuthState } from 'state/useAuthState';
import {
  getAckedByUsers,
  getNewestAckRequest,
  getUserCanAck,
  postAck,
} from './ExternalGeoEventAck.utils';

type ExternalGeoEventAckProps = {
  event: ExternalGeoEvent;
};

export const ExternalGeoEventAck = ({
  event,
}: ExternalGeoEventAckProps): JSX.Element | null => {
  const { user } = useAuthState();
  const { t } = useTranslation();
  const invalidateQuery = useInvalidateExternalGeoEventQuery(event.id);

  const ackedByUsers = getAckedByUsers(event);
  const newestAckRequest = getNewestAckRequest(event);
  const userCanAck =
    newestAckRequest && user ? getUserCanAck(newestAckRequest, user) : false;

  const ackMutation = useMutation({
    mutationFn: (ackRequest: RequestedAck) => postAck(ackRequest.id),
    onSuccess: () => {
      enqueueSnackbar(t('externalGeoEvent.ackPosted'), { variant: 'success' });
      return invalidateQuery();
    },
    onError: () =>
      enqueueSnackbar(t('common.errorMessage'), { variant: 'error' }),
  });

  if (!ackedByUsers && !userCanAck) return null;

  return (
    <Stack sx={{ py: 1, px: 2 }}>
      {ackedByUsers && (
        <Typography>
          <Trans i18nKey="externalGeoEvent.ackedBy" values={{ ackedByUsers }}>
            Acknowledged by <b>ackedByUsers</b>
          </Trans>
        </Typography>
      )}
      {userCanAck && (
        <Button
          color="success"
          disabled={ackMutation.isPending}
          fullWidth
          onClick={() =>
            newestAckRequest && ackMutation.mutate(newestAckRequest)
          }
          size="large"
          sx={{
            fontWeight: 'fontWeightBold',
            mt: 2,
            textTransform: 'uppercase',
          }}
        >
          {t('externalGeoEvent.ack')}
        </Button>
      )}
    </Stack>
  );
};
